import React, { useState } from 'react'

/**
 * Add Page Dialog Component
 * Handles the dialog for adding new pages to the template
 * Supports both manual page creation and JSON import
 */
const AddPageDialog = ({
  showAddPageDialog,
  setShowAddPageDialog,
  addPageConfig,
  setAddPageConfig,
  template_content,
  settemplate_content,
  currentPageIndex,
  setCurrentPageIndex
}) => {
  const [activeTab, setActiveTab] = useState('manual') // 'manual' or 'json'
  const [jsonInput, setJsonInput] = useState('')
  const [jsonError, setJsonError] = useState('')

  if (!showAddPageDialog) {
    return null
  }

  const handleAddPage = () => {
    const newPage = {
      id: `page-${Date.now()}`,
      page_config: {
        width: Math.max(1, addPageConfig.width || 800),
        height: Math.max(1, addPageConfig.height || 600),
        backgroundColor: addPageConfig.backgroundColor || '#ffffff',
        name: `Page ${(template_content?.pages?.length || 0) + 1}`,
        locked: false,
      },
      items: []
    }

    settemplate_content(prev => ({
      ...prev,
      pages: prev.pages && prev.pages.length
        ? [
          ...prev.pages.slice(0, currentPageIndex + 1),
          newPage,
          ...prev.pages.slice(currentPageIndex + 1)
        ]
        : [newPage]
    }))

    setShowAddPageDialog(false)
    resetDialog()

    if (!template_content?.pages || template_content.pages.length === 0) {
      setCurrentPageIndex(0)
    }
  }

  const handlePasteJSON = () => {
    try {
      setJsonError('')

      // Parse the JSON
      const parsedData = JSON.parse(jsonInput)

      // Validate that it's an array of pages or a single page
      let pagesToAdd = []

      if (Array.isArray(parsedData)) {
        // It's an array of pages
        pagesToAdd = parsedData
      } else if (parsedData.pages && Array.isArray(parsedData.pages)) {
        // It's a template object with pages array
        pagesToAdd = parsedData.pages
      } else if (parsedData.page_config || parsedData.items) {
        // It's a single page object
        pagesToAdd = [parsedData]
      } else {
        throw new Error('Invalid JSON format. Expected pages array or page object.')
      }

      // Validate each page has required structure
      const validatedPages = pagesToAdd.map((page, index) => {
        if (!page.page_config && !page.items) {
          throw new Error(`Page ${index + 1} is missing required structure (page_config or items)`)
        }

        return {
          id: page.id || `page-${Date.now()}-${index}`,
          page_config: {
            width: page.page_config?.width || 800,
            height: page.page_config?.height || 600,
            backgroundColor: page.page_config?.backgroundColor || '#ffffff',
            name: page.page_config?.name || `Page ${(template_content?.pages?.length || 0) + index + 1}`,
            locked: page.page_config?.locked || false,
            ...page.page_config
          },
          items: Array.isArray(page.items) ? page.items : []
        }
      })

      // Add the pages to the template
      settemplate_content(prev => ({
        ...prev,
        pages: prev.pages && prev.pages.length
          ? [
            ...prev.pages.slice(0, currentPageIndex + 1),
            ...validatedPages,
            ...prev.pages.slice(currentPageIndex + 1)
          ]
          : validatedPages
      }))

      setShowAddPageDialog(false)
      resetDialog()

      // Show success message
      alert(`Successfully added ${validatedPages.length} page(s)!`)

    } catch (error) {
      setJsonError(error.message || 'Invalid JSON format')
      console.error('JSON Parse Error:', error)
    }
  }

  const resetDialog = () => {
    setActiveTab('manual')
    setJsonInput('')
    setJsonError('')
  }

  const handleClose = () => {
    setShowAddPageDialog(false)
    resetDialog()
  }

  return (
    <div className="fixed inset-0 z-[550] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 pb-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Add New Page</h3>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={handleClose}
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('manual')}
              className={`px-4 py-2 font-medium text-sm transition-colors ${
                activeTab === 'manual'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Manual Setup
            </button>
            <button
              onClick={() => setActiveTab('json')}
              className={`px-4 py-2 font-medium text-sm transition-colors ${
                activeTab === 'json'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Paste JSON Config
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'manual' ? (
            <div className="space-y-4">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block text-sm text-gray-600 mb-1">Width</label>
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2"
                    min="1"
                    value={addPageConfig.width}
                    onChange={(e) => setAddPageConfig(prev => ({
                      ...prev,
                      width: parseInt(e.target.value || '0', 10)
                    }))}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-gray-600 mb-1">Height</label>
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2"
                    min="1"
                    value={addPageConfig.height}
                    onChange={(e) => setAddPageConfig(prev => ({
                      ...prev,
                      height: parseInt(e.target.value || '0', 10)
                    }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Background Color</label>
                <input
                  type="color"
                  className="w-16 h-10 border rounded"
                  value={addPageConfig.backgroundColor}
                  onChange={(e) => setAddPageConfig(prev => ({
                    ...prev,
                    backgroundColor: e.target.value
                  }))}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Paste JSON Configuration
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Paste the JSON content copied from "View Content" → "Copy" button.
                  You can paste a single page, multiple pages array, or full template JSON.
                </p>
                <textarea
                  className="w-full h-64 px-3 py-2 border rounded font-mono text-sm"
                  placeholder='Paste JSON here, e.g.:
{
  "pages": [
    {
      "page_config": { "width": 800, "height": 600 },
      "items": [...]
    }
  ]
}'
                  value={jsonInput}
                  onChange={(e) => {
                    setJsonInput(e.target.value)
                    setJsonError('')
                  }}
                />
                {jsonError && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    <strong>Error:</strong> {jsonError}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-6 pt-0 flex justify-end space-x-2 border-t border-gray-200">
          <button
            className="px-4 py-2 rounded border hover:bg-gray-50"
            onClick={handleClose}
          >
            Cancel
          </button>
          {activeTab === 'manual' ? (
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={handleAddPage}
            >
              Add Page
            </button>
          ) : (
            <button
              className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={handlePasteJSON}
              disabled={!jsonInput.trim()}
            >
              Import Pages
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AddPageDialog
